import {
  MortgageFormData,
  LinearMortgageFormData,
  MortgageResult,
  LinearMortgageResult,
  YearlyBreakdown,
  MonthlyPayment,
  EnergyLabel,
  MortgageCalculationParams,
  CalculationBreakdown,
  MaxMortgageBreakdown,
  MortgageComparison
} from '../types';

// Utility functions for number formatting
export const parseNumber = (str: string): number => {
  return parseFloat(str.replace(/[^\d,]/g, '').replace(/,/g, '.')) || 0;
};

export const formatInputNumber = (str: string): string => {
  const d = str.replace(/\D/g, '');
  return d.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

export const formatDisplayNumber = (x: number): string => {
  return x.toLocaleString('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const formatCurrency = (amount: number): string => {
  return `€ ${formatDisplayNumber(amount)}`;
};

// Calculate eigenwoningforfait (owner-occupied property imputed rental value)
export const calculateEigenwoningforfait = (houseValue: number): number => {
  if (houseValue <= 12500) {
    return 0;
  } else if (houseValue <= 25000) {
    return (houseValue - 12500) * 0.001;
  } else if (houseValue <= 50000) {
    return 125 + (houseValue - 25000) * 0.002;
  } else if (houseValue <= 75000) {
    return 125 + 500 + (houseValue - 50000) * 0.0025;
  } else if (houseValue <= 1330000) {
    return 125 + 500 + 625 + (houseValue - 75000) * 0.0035;
  } else {
    return 4655 + (houseValue - 1330000) * 0.0235;
  }
};

// Energy label bonuses
const ENERGY_LABEL_BONUSES: Record<EnergyLabel, number> = {
  'G': 0,
  'F': 0,
  'E': 0,
  'D': 5000,
  'C': 5000,
  'B': 10000,
  'A': 10000,
  'A+': 20000,
  'A++': 20000,
  'A+++': 30000,
  'A++++': 40000,
  'A++++ garantie': 50000
};

// Calculate maximum mortgage amount
export const calculateMaxMortgage = (params: MortgageCalculationParams): number => {
  const { age, grossIncome, monthlyObligations, interestRate, termYears, energyLabel } = params;
  const months = termYears * 12;

  // Calculate income percentage (20% base + up to 8% extra based on income)
  let incomePercentage = 0.20 + Math.min((grossIncome - 20000) / 400000, 0.08);
  incomePercentage = Math.min(incomePercentage, 0.28);

  // Calculate yearly available income for mortgage
  let yearlyAvailable = grossIncome * incomePercentage - monthlyObligations * 12;
  yearlyAvailable = Math.max(yearlyAvailable, 0);

  const monthlyAvailable = yearlyAvailable / 12;
  const monthlyRate = interestRate / 12;

  // Calculate annuity factor
  const annuityFactor = monthlyRate / (1 - Math.pow(1 + monthlyRate, -months));

  // Calculate maximum mortgage
  let maxMortgage = monthlyAvailable / annuityFactor;

  // Apply age reduction for 57+
  if (age >= 57) {
    maxMortgage *= 0.7;
  }

  // Add energy label bonus
  const energyBonus = ENERGY_LABEL_BONUSES[energyLabel] || 0;

  return maxMortgage + energyBonus;
};

// Calculate annuity payment
export const calculateAnnuityPayment = (principal: number, rate: number, months: number): number => {
  const monthlyRate = rate / 12;
  if (monthlyRate === 0) return principal / months;
  return principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -months));
};

// Calculate breakdown for specific month
export const calculateBreakdown = (
  principal: number,
  rate: number,
  months: number,
  targetMonth: number
): CalculationBreakdown => {
  let balance = principal;
  let interest = 0;
  let principalPayment = 0;
  const monthlyRate = rate / 12;
  const annuityPayment = calculateAnnuityPayment(principal, rate, months);

  for (let month = 1; month <= targetMonth; month++) {
    interest = balance * monthlyRate;
    principalPayment = annuityPayment - interest;
    balance -= principalPayment;
  }

  return {
    interest,
    principal: principalPayment,
    balance: Math.max(0, balance)
  };
};

// Calculate annuity mortgage results
export const calculateAnnuityMortgage = (formData: MortgageFormData): MortgageResult => {
  const {
    mortgageAmount,
    term,
    rate,
    buyerCosts,
    useNHG,
    nhgDiscount,
    useMaxMortgage
  } = formData;

  let interestRate = rate / 100;

  // Apply NHG discount
  if (useNHG) {
    interestRate = Math.max(0, interestRate - nhgDiscount / 100);
  }

  // Determine mortgage amount to use
  let finalMortgageAmount = mortgageAmount;
  if (useMaxMortgage) {
    finalMortgageAmount = calculateMaxMortgageAmount(formData);
  }

  const months = term * 12;
  const monthlyPayment = calculateAnnuityPayment(finalMortgageAmount, interestRate, months);
  const totalPayment = monthlyPayment * months;
  const totalInterest = totalPayment - finalMortgageAmount;

  // Create yearly breakdown for each year
  const yearlyBreakdown: YearlyBreakdown[] = [];
  for (let year = 1; year <= term; year++) {
    const breakdown = calculateBreakdown(finalMortgageAmount, interestRate, months, Math.min(year * 12, months));
    const mortgageDeduction = breakdown.interest * (formData.mortgageDeductionRate / 100);
    const netPayment = monthlyPayment - mortgageDeduction;
    const eigenwoningforfait = calculateEigenwoningforfait(formData.houseValue) / 12;
    const netPaymentWithEWF = netPayment + eigenwoningforfait;
    yearlyBreakdown.push({
      year,
      interest: breakdown.interest,
      principal: breakdown.principal,
      balance: breakdown.balance,
      monthlyPayment,
      mortgageDeduction,
      netPayment,
      eigenwoningforfait,
      netPaymentWithEWF
    });
  }

  return {
    percentage: 1.0,
    mortgageAmount: finalMortgageAmount,
    buyerCosts: finalMortgageAmount * (buyerCosts / 100),
    monthlyPayment,
    totalInterest,
    totalPayment,
    yearlyBreakdown
  };
};

// Calculate linear mortgage payment for specific month
export const calculateLinearPayment = (
  principal: number,
  rate: number,
  months: number,
  month: number,
  mortgageDeductionRate: number = 37.48,
  houseValue: number = 0
): MonthlyPayment => {
  const monthlyRate = rate / 12;
  const monthlyPrincipal = principal / months;
  const remainingBalance = principal - (monthlyPrincipal * (month - 1));
  const interest = remainingBalance * monthlyRate;
  const totalPayment = monthlyPrincipal + interest;
  const balance = Math.max(0, remainingBalance - monthlyPrincipal);
  const mortgageDeduction = interest * (mortgageDeductionRate / 100);
  const netPayment = totalPayment - mortgageDeduction;
  const eigenwoningforfait = calculateEigenwoningforfait(houseValue) / 12;
  const netPaymentWithEWF = netPayment + eigenwoningforfait;

  return {
    month,
    interest,
    principal: monthlyPrincipal,
    balance,
    totalPayment,
    mortgageDeduction,
    netPayment,
    eigenwoningforfait,
    netPaymentWithEWF
  };
};

// Calculate linear mortgage results
export const calculateLinearMortgage = (formData: LinearMortgageFormData): LinearMortgageResult => {
  const {
    mortgageAmount,
    rate,
    term,
    mortgageDeductionRate,
    houseValue,
    useMaxMortgage,
    useNHG,
    nhgDiscount
  } = formData;

  let interestRate = rate / 100;

  // Apply NHG discount
  if (useNHG) {
    interestRate = Math.max(0, interestRate - nhgDiscount / 100);
  }

  // Determine mortgage amount to use
  let finalMortgageAmount = mortgageAmount;
  if (useMaxMortgage) {
    finalMortgageAmount = calculateMaxLinearMortgageAmount(formData);
  }

  const months = term * 12;

  const monthlyPayments: MonthlyPayment[] = [];
  let totalInterest = 0;

  for (let month = 1; month <= months; month++) {
    const payment = calculateLinearPayment(finalMortgageAmount, interestRate, months, month, mortgageDeductionRate, houseValue);
    monthlyPayments.push(payment);
    totalInterest += payment.interest;
  }

  const totalPayment = finalMortgageAmount + totalInterest;
  const averageMonthlyPayment = totalPayment / months;

  return {
    mortgageAmount: finalMortgageAmount,
    monthlyPayments,
    totalInterest,
    totalPayment,
    averageMonthlyPayment
  };
};

// Calculate NHG fee
export const calculateNHGFee = (mortgageAmount: number, feeRate: number): number => {
  return mortgageAmount * (feeRate / 100);
};

// Generate interest rate scenarios
export const generateInterestRateScenarios = (
  baseRate: number,
  mortgageAmount: number,
  term: number
): { rate: number; monthlyPayment: number }[] => {
  const scenarios = [];
  const rateVariations = [-1, -0.5, 0, 0.5, 1, 1.5, 2];

  for (const variation of rateVariations) {
    const rate = Math.max(0.1, baseRate + variation) / 100;
    const monthlyPayment = calculateAnnuityPayment(mortgageAmount, rate, term * 12);
    scenarios.push({
      rate: (rate * 100),
      monthlyPayment
    });
  }

  return scenarios;
};

// Calculate maximum mortgage based on Dutch mortgage rules
// Usage: const maxMortgage = calculateMaxMortgageAmount(formData);
export const calculateMaxMortgageAmount = (formData: MortgageFormData): number => {
  const {
    age,
    income,
    partnerIncome,
    liabilities,
    term,
    rate,
    energyLabel,
    useNHG,
    nhgLimit,
    nhgDiscount,
    houseValue
  } = formData;

  const totalIncome = income + partnerIncome;
  let interestRate = rate / 100;

  // Apply NHG discount
  if (useNHG) {
    interestRate = Math.max(0, interestRate - nhgDiscount / 100);
  }

  // Calculate maximum mortgage using standard Dutch mortgage calculation
  const maxMortgage = calculateMaxMortgage({
    age,
    grossIncome: totalIncome,
    monthlyObligations: liabilities,
    interestRate,
    termYears: term,
    energyLabel
  });

  let finalMaxMortgage = maxMortgage;

  // Apply NHG limit if applicable
  if (useNHG && finalMaxMortgage > nhgLimit) {
    finalMaxMortgage = nhgLimit;
  }

  // Apply house value limit (100% LTV maximum)
  if (finalMaxMortgage > houseValue) {
    finalMaxMortgage = houseValue;
  }

  return Math.max(0, finalMaxMortgage);
};

// Calculate maximum mortgage for linear mortgage based on Dutch mortgage rules
// Linear mortgages have higher initial payments, so maximum amount is typically lower
// Usage: const maxMortgage = calculateMaxLinearMortgageAmount(formData);
export const calculateMaxLinearMortgageAmount = (formData: LinearMortgageFormData): number => {
  const {
    age,
    income,
    partnerIncome,
    liabilities,
    term,
    rate,
    energyLabel,
    useNHG,
    nhgLimit,
    nhgDiscount,
    houseValue
  } = formData;

  const totalIncome = income + partnerIncome;
  let interestRate = rate / 100;

  // Apply NHG discount
  if (useNHG) {
    interestRate = Math.max(0, interestRate - nhgDiscount / 100);
  }

  // For linear mortgages, calculate based on initial payment burden (highest payment)
  // Initial payment = (Principal/months) + (Principal * monthlyRate)
  const months = term * 12;

  // Calculate income percentage (20% base + up to 8% extra based on income)
  let incomePercentage = 0.20 + Math.min((totalIncome - 20000) / 400000, 0.08);
  incomePercentage = Math.min(incomePercentage, 0.28);

  // Calculate yearly available income for mortgage
  let yearlyAvailable = totalIncome * incomePercentage - liabilities * 12;
  yearlyAvailable = Math.max(yearlyAvailable, 0);

  const monthlyAvailable = yearlyAvailable / 12;
  const monthlyRate = interestRate / 12;

  // For linear mortgage, initial payment = (Principal/months) + (Principal * monthlyRate)
  // Solve for Principal: monthlyAvailable = (P/months) + (P * monthlyRate)
  // monthlyAvailable = P * (1/months + monthlyRate)
  // P = monthlyAvailable / (1/months + monthlyRate)
  let maxMortgageLinear = monthlyAvailable / ((1 / months) + monthlyRate);

  // Apply age reduction for 57+
  if (age >= 57) {
    maxMortgageLinear *= 0.7;
  }

  // Add energy label bonus
  const energyBonus = ENERGY_LABEL_BONUSES[energyLabel] || 0;
  maxMortgageLinear += energyBonus;

  let finalMaxMortgage = maxMortgageLinear;

  // Apply NHG limit if applicable
  if (useNHG && finalMaxMortgage > nhgLimit) {
    finalMaxMortgage = nhgLimit;
  }

  // Apply house value limit (100% LTV maximum)
  if (finalMaxMortgage > houseValue) {
    finalMaxMortgage = houseValue;
  }

  return Math.max(0, finalMaxMortgage);
};

// Compare maximum mortgage amounts between annuity and linear mortgages
export const compareMortgageTypes = (formData: MortgageFormData): MortgageComparison => {
  // Calculate annuity maximum
  const annuityMax = calculateMaxMortgageAmount(formData);

  // Convert to linear form data for calculation
  const linearFormData: LinearMortgageFormData = {
    ...formData,
    mortgageAmount: annuityMax // This won't be used in max calculation
  };

  // Calculate linear maximum
  const linearMax = calculateMaxLinearMortgageAmount(linearFormData);

  let interestRate = formData.rate / 100;
  if (formData.useNHG) {
    interestRate = Math.max(0, interestRate - formData.nhgDiscount / 100);
  }

  // Calculate initial payments for comparison
  const months = formData.term * 12;
  const annuityInitialPayment = calculateAnnuityPayment(annuityMax, interestRate, months);
  const linearInitialPayment = (linearMax / months) + (linearMax * (interestRate / 12));

  return {
    annuityMax,
    linearMax,
    difference: annuityMax - linearMax,
    annuityInitialPayment,
    linearInitialPayment
  };
};

// Generic maximum mortgage calculation with detailed breakdown
// Usage: const breakdown = calculateMaxMortgageBreakdown(totalIncome, liabilities, age, rate, term, energyLabel, houseValue, useNHG, nhgLimit);
export const calculateMaxMortgageBreakdown = (
  totalIncome: number,
  monthlyLiabilities: number,
  age: number,
  interestRate: number,
  termYears: number,
  energyLabel: EnergyLabel,
  houseValue: number,
  useNHG: boolean = false,
  nhgLimit: number = 435000
): MaxMortgageBreakdown => {
  const months = termYears * 12;

  // Calculate income percentage (20% base + up to 8% extra based on income)
  let incomePercentage = 0.20 + Math.min((totalIncome - 20000) / 400000, 0.08);
  incomePercentage = Math.min(incomePercentage, 0.28);

  // Calculate yearly available income for mortgage
  let yearlyAvailable = totalIncome * incomePercentage - monthlyLiabilities * 12;
  yearlyAvailable = Math.max(yearlyAvailable, 0);

  const monthlyAvailable = yearlyAvailable / 12;
  const monthlyRate = interestRate / 12;

  // Calculate annuity factor
  const annuityFactor = monthlyRate / (1 - Math.pow(1 + monthlyRate, -months));

  // Calculate base maximum mortgage
  let baseMaxMortgage = monthlyAvailable / annuityFactor;

  // Energy label bonus
  const energyBonus = ENERGY_LABEL_BONUSES[energyLabel] || 0;

  // Apply age reduction for 57+
  const ageReduction = age >= 57 ? 0.3 : 0; // 30% reduction
  const ageAdjustedMortgage = baseMaxMortgage * (1 - ageReduction);

  // Calculate mortgage with energy bonus
  let maxMortgageWithBonus = ageAdjustedMortgage + energyBonus;

  let finalMaxMortgage = maxMortgageWithBonus;
  let limitingFactor: 'income' | 'age' | 'nhg' | 'houseValue' | 'none' = 'none';
  let nhgLimitApplied = null;
  let houseLimitApplied = false;

  // Apply NHG limit if applicable
  if (useNHG && maxMortgageWithBonus > nhgLimit) {
    finalMaxMortgage = nhgLimit;
    limitingFactor = 'nhg';
    nhgLimitApplied = nhgLimit;
  }

  // Apply house value limit (100% LTV maximum)
  if (finalMaxMortgage > houseValue) {
    finalMaxMortgage = houseValue;
    limitingFactor = 'houseValue';
    houseLimitApplied = true;
  }

  // Determine limiting factor if not already set
  if (limitingFactor === 'none') {
    if (age >= 57) {
      limitingFactor = 'age';
    } else {
      limitingFactor = 'income';
    }
  }

  return {
    baseMaxMortgage: Math.max(0, baseMaxMortgage),
    energyLabelBonus: energyBonus,
    ageReduction: ageReduction,
    nhgLimit: nhgLimitApplied,
    houseLimitApplied,
    finalMaxMortgage: Math.max(0, finalMaxMortgage),
    limitingFactor
  };
};

// Utility function to get maximum mortgage with formatted display
// Usage: const { maxMortgage, formattedAmount, breakdown } = getMaxMortgageDisplay(formData);
// Example: console.log(`Maximum mortgage: ${formattedAmount}, Limited by: ${breakdown.limitingFactor}`);
export const getMaxMortgageDisplay = (formData: MortgageFormData): {
  maxMortgage: number;
  formattedAmount: string;
  breakdown: MaxMortgageBreakdown;
} => {
  const {
    income,
    partnerIncome,
    liabilities,
    age,
    rate,
    term,
    energyLabel,
    houseValue,
    useNHG,
    nhgLimit,
    nhgDiscount
  } = formData;

  const totalIncome = income + partnerIncome;
  let interestRate = rate / 100;

  // Apply NHG discount
  if (useNHG) {
    interestRate = Math.max(0, interestRate - nhgDiscount / 100);
  }

  const breakdown = calculateMaxMortgageBreakdown(
    totalIncome,
    liabilities,
    age,
    interestRate,
    term,
    energyLabel,
    houseValue,
    useNHG,
    nhgLimit
  );

  return {
    maxMortgage: breakdown.finalMaxMortgage,
    formattedAmount: formatCurrency(breakdown.finalMaxMortgage),
    breakdown
  };
};

// Validate form data
export const validateMortgageForm = (formData: MortgageFormData): string[] => {
  const errors: string[] = [];

  if (!formData.age || formData.age < 18 || formData.age > 75) {
    errors.push('Leeftijd moet tussen 18 en 75 jaar zijn');
  }

  if (!formData.income || formData.income <= 0) {
    errors.push('Bruto jaarinkomen is verplicht');
  }

  if (!formData.useMaxMortgage) {
    if (!formData.mortgageAmount || formData.mortgageAmount <= 0) {
      errors.push('Hypotheekbedrag is verplicht');
    }

    if (formData.mortgageAmount > formData.houseValue) {
      errors.push('Hypotheekbedrag kan niet hoger zijn dan de huiswaarde');
    }
  }

  if (formData.partnerIncome < 0) {
    errors.push('Inkomen van partner kan niet negatief zijn');
  }

  if (formData.liabilities < 0) {
    errors.push('Bestaande verplichtingen kunnen niet negatief zijn');
  }

  if (!formData.term || formData.term <= 0 || formData.term > 30) {
    errors.push('Looptijd moet tussen 1 en 30 jaar zijn');
  }

  if (!formData.rate || formData.rate < 0 || formData.rate > 20) {
    errors.push('Rente moet tussen 0% en 20% zijn');
  }

  if (formData.buyerCosts < 0 || formData.buyerCosts > 10) {
    errors.push('Kosten koper moet tussen 0% en 10% zijn');
  }

  if (formData.mortgageDeductionRate < 0 || formData.mortgageDeductionRate > 100) {
    errors.push('Hypotheekrente aftrek moet tussen 0% en 100% zijn');
  }

  if (!formData.houseValue || formData.houseValue <= 0) {
    errors.push('Huiswaarde is verplicht en moet groter dan 0 zijn');
  }

  return errors;
};

export const validateLinearMortgageForm = (formData: LinearMortgageFormData): string[] => {
  const errors: string[] = [];

  if (!formData.age || formData.age < 18 || formData.age > 75) {
    errors.push('Leeftijd moet tussen 18 en 75 jaar zijn');
  }

  if (!formData.useMaxMortgage) {
    if (!formData.mortgageAmount || formData.mortgageAmount <= 0) {
      errors.push('Hypotheekbedrag is verplicht');
    }

    if (formData.mortgageAmount > formData.houseValue) {
      errors.push('Hypotheekbedrag kan niet hoger zijn dan de huiswaarde');
    }
  }

  if (formData.partnerIncome < 0) {
    errors.push('Inkomen van partner kan niet negatief zijn');
  }

  if (!formData.term || formData.term <= 0 || formData.term > 30) {
    errors.push('Looptijd moet tussen 1 en 30 jaar zijn');
  }

  if (!formData.rate || formData.rate < 0 || formData.rate > 20) {
    errors.push('Rente moet tussen 0% en 20% zijn');
  }

  if (formData.mortgageDeductionRate < 0 || formData.mortgageDeductionRate > 100) {
    errors.push('Hypotheekrente aftrek moet tussen 0% en 100% zijn');
  }

  if (!formData.houseValue || formData.houseValue <= 0) {
    errors.push('Huiswaarde is verplicht en moet groter dan 0 zijn');
  }

  return errors;
};
